import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, Icon as IconHoi, useTheme } from '@Sage/hoi-poi-ui';

import Icon from 'shards/Imports/icons/Icon';
import { SidebarContext } from './SidebarContext';

import styles from '../styles/MenuSubmenu';
const useStyles = createUseStyles(styles, 'MenuSubmenu');

const matchHref = (currentHref, link) => {
    if (!link || !currentHref) return false;

    return (
        currentHref.includes(link) ||
        link === `..${window.location.pathname}` ||
        (window.location.hash &&
            window.location.hash !== '#/' &&
            link?.includes(window.location.hash))
    );
};

const MenuSubmenu = memo(
    ({
        className: classNameProp,
        currentHref = '',
        icon = '',
        isActive = false,
        name = '',
        onMatchActive,
        submenuItems = [],
    }) => {
        const containerRef = useRef(null);
        const [isOpen, setIsOpen] = useState(false);
        const [flyoutTop, setFlyoutTop] = useState(0);
        const [flyoutLeft, setFlyoutLeft] = useState(0);
        const classes = useStyles();
        const theme = useTheme();
        const { isExpanded, isMobile } = useContext(SidebarContext);

        const updateFlyoutPosition = useCallback(() => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setFlyoutTop(rect.top);
            setFlyoutLeft(rect.right);
        }, []);

        const handleOpen = useCallback(() => {
            updateFlyoutPosition();
            setIsOpen(true);
        }, [updateFlyoutPosition]);

        const handleClose = useCallback(() => {
            setIsOpen(false);
        }, []);

        const handleParentClick = useCallback(
            (e) => {
                if (!isMobile) return;
                e.preventDefault();
                setIsOpen((prev) => !prev);
                updateFlyoutPosition();
            },
            [isMobile, updateFlyoutPosition],
        );

        const handleSubmenuClick = useCallback(
            (item) => (e) => {
                e.preventDefault();
                const url = `${window.location.origin}${item.link}`;
                if (e.ctrlKey || e.metaKey || e.button === 1 || e.shiftKey) {
                    window.open(url, '_blank');
                    return;
                }

                item.onClick && item.onClick();
                if (item.navigate) {
                    item.navigate(e);
                }
                setIsOpen(false);
            },
            [],
        );

        const isSubmenuItemActive = useCallback(
            (item) => {
                if (onMatchActive) return onMatchActive(item);
                return matchHref(currentHref, item.link);
            },
            [currentHref, onMatchActive],
        );

        const parentClassName = classnames(
            classes.parent,
            {
                [classes.parentActive]: isActive,
                [classes.parentOpen]: isOpen,
                [classes.parentExpanded]: isExpanded,
            },
            classNameProp,
        );

        const flyoutContent = useMemo(
            () =>
                submenuItems.map((item, index) => {
                    const itemActive = isSubmenuItemActive(item);

                    return (
                        <a
                            key={`submenu-item-${item.link || index}`}
                            href={item.link}
                            className={classnames(classes.submenuItem, {
                                [classes.submenuItemActive]: itemActive,
                            })}
                            onClick={handleSubmenuClick(item)}
                            aria-current={itemActive ? 'page' : undefined}
                        >
                            <Icon
                                name={item.icon}
                                className={classes.submenuIcon}
                                color={theme.colors.primary.white}
                            />
                            <span className={classes.submenuLabel}>{item.name}</span>
                        </a>
                    );
                }),
            [
                classes.submenuIcon,
                classes.submenuItem,
                classes.submenuItemActive,
                classes.submenuLabel,
                handleSubmenuClick,
                isSubmenuItemActive,
                submenuItems,
                theme.colors.primary.white,
            ],
        );

        return (
            <div
                ref={containerRef}
                className={classes.root}
                onMouseEnter={!isMobile ? handleOpen : undefined}
                onMouseLeave={!isMobile ? handleClose : undefined}
            >
                <div
                    className={parentClassName}
                    onClick={handleParentClick}
                    role="button"
                    tabIndex={0}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                >
                    <Icon name={icon} className={classes.icon} color={theme.colors.primary.white} />
                    <span
                        className={classnames(classes.label, {
                            [classes.labelExpanded]: isExpanded,
                        })}
                    >
                        {name}
                    </span>
                    <IconHoi
                        name="chevronRight"
                        className={classnames(classes.caret, {
                            [classes.caretExpanded]: isExpanded,
                        })}
                        color={theme.colors.primary.white}
                    />
                </div>
                {isOpen && (
                    <div
                        className={classes.flyout}
                        style={{ top: flyoutTop, left: flyoutLeft }}
                        onMouseEnter={!isMobile ? handleOpen : undefined}
                        onMouseLeave={!isMobile ? handleClose : undefined}
                    >
                        {flyoutContent}
                    </div>
                )}
            </div>
        );
    },
);

MenuSubmenu.propTypes = {
    className: PropTypes.string,
    currentHref: PropTypes.string,
    icon: PropTypes.string,
    isActive: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onMatchActive: PropTypes.func,
    submenuItems: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string,
            link: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            navigate: PropTypes.func,
            onClick: PropTypes.func,
        }),
    ).isRequired,
};

export default MenuSubmenu;
